import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import EpisodeCard from "@/components/EpisodeCard";
import ShareBar from "@/components/ShareBar";
import TopicOfWeek from "@/components/TopicOfWeek";
import BundleBanner from "@/components/BundleBanner";
import RedeemCodeModal from "@/components/RedeemCodeModal";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { Link, useLocation } from "react-router-dom";
import { Ticket } from "lucide-react";

export default function Episodes() {
  const { user } = useAuth();
  const { current } = usePlayer();
  const location = useLocation();
  const [eps, setEps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);
  const [expandedNumber, setExpandedNumber] = useState(null);
  const [showRedeem, setShowRedeem] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/episodes");
      setEps(data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, user, reloadTick]);

  // Scroll to hash target (e.g. #topic-of-the-week) once the page has content
  useEffect(() => {
    if (loading) return;
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    // small delay so React finishes rendering TopicOfWeek's async content
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [loading, location.hash]);

  // Auto-collapse the expanded card whenever a different episode starts playing
  useEffect(() => {
    if (current?.number && expandedNumber && current.number !== expandedNumber) {
      setExpandedNumber(null);
    }
  }, [current?.number, expandedNumber]);

  const bump = () => setReloadTick((t) => t + 1);
  const toggleExpand = (number) => setExpandedNumber((cur) => (cur === number ? null : number));
  const free = eps.filter(e => e.is_free && e.kind !== "recap");
  const paid = eps.filter(e => !e.is_free && e.kind !== "recap");
  const recaps = eps.filter(e => e.kind === "recap").sort((a, b) => a.sort_index - b.sort_index);

  // Interleave recaps after their gate episode
  const renderWithRecaps = (list) => {
    const out = [];
    list.forEach((ep, i) => {
      out.push(
        <EpisodeCard
          key={ep.id}
          ep={ep}
          idx={i}
          onUnlocked={bump}
          isExpanded={expandedNumber === ep.number}
          onToggleExpand={() => toggleExpand(ep.number)}
        />
      );
      const recap = recaps.find(r => r.gate_episode === ep.number);
      if (recap) {
        out.push(
          <EpisodeCard
            key={recap.id}
            ep={recap}
            idx={i}
            onUnlocked={bump}
            isExpanded={expandedNumber === recap.number}
            onToggleExpand={() => toggleExpand(recap.number)}
          />
        );
      }
    });
    return out;
  };

  return (
    <div className="bg-ink min-h-screen has-player" data-testid="episodes-page">
      <div className="max-w-5xl mx-auto px-5 md:px-10 py-12 md:py-16">

        <div
          className="bg-coal border-l-4 border-hotpink p-4 md:p-5 mb-8 flex items-start gap-3"
          data-testid="content-warning"
          role="alert"
        >
          <span
            className="font-outfit font-extrabold text-hotpink text-xs uppercase tracking-widest mt-0.5 shrink-0"
            aria-hidden="true"
          >
            ⚠ Warning
          </span>
          <p className="font-outfit text-white/80 text-sm md:text-base leading-snug">
            This series contains <span className="text-hotpink font-bold">explicit language</span> and <span className="text-hotpink font-bold">sexual content</span>. Headphones recommended. Not for children.
          </p>
        </div>

        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <p className="scribble text-hotpink text-2xl rotate-[-2deg] inline-block mb-3">tonight's recap:</p>
            <h1 className="brush-title text-5xl md:text-7xl text-white">
              THE <span className="text-hotpink">EPISODES.</span>
            </h1>
          </div>
        </div>

        <p className="font-outfit text-white/60 max-w-2xl mb-4">
          Listen in order — chaos has a chronology. First ten are on the house. After that, every dollar buys you another disaster.
        </p>
        {!user && (
          <p className="scribble text-white/70 text-xl mb-4">
            <Link to="/signup" className="text-hotpink underline underline-offset-4" data-testid="signup-link-in-episodes">make an account</Link> to save your spot &amp; unlock paid episodes.
          </p>
        )}

        <button
          onClick={() => setShowRedeem(true)}
          data-testid="open-redeem-modal"
          className="mb-8 inline-flex items-center gap-2 border-2 border-hotpink/60 hover:border-hotpink hover:bg-hotpink/10 text-white font-outfit font-bold uppercase tracking-widest text-xs px-4 py-2 transition-colors"
        >
          <Ticket size={14} className="text-hotpink"/> Have a gift code?
        </button>

        {user && (
          <div className="mb-8">
            <ShareBar onClaimed={bump} />
          </div>
        )}

        <div id="topic-of-the-week" className="scroll-mt-24">
          <TopicOfWeek />
        </div>

        {loading && <p className="text-white/50 font-outfit" data-testid="episodes-loading">loading the diary...</p>}

        {!loading && (
          <>
            <div className="mb-8">
              <h2 className="font-outfit uppercase tracking-[0.3em] text-xs text-white/40 mb-4">free · episodes 1–10</h2>
              <div className="space-y-4">
                {renderWithRecaps(free)}
              </div>
            </div>

            <div>
              <BundleBanner user={user} paidEps={paid} onPurchased={bump} />
              <h2 className="font-outfit uppercase tracking-[0.3em] text-xs text-white/40 mb-4">
                <span className="text-hotpink">$1 each ·</span> the rest
              </h2>
              <div className="space-y-4">
                {renderWithRecaps(paid)}
              </div>
            </div>
          </>
        )}
      </div>
      <RedeemCodeModal open={showRedeem} onClose={() => setShowRedeem(false)} onRedeemed={bump} />
    </div>
  );
}
